<script setup lang="ts">
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from 'reka-ui';
import { X } from 'lucide-vue-next';
import { cn } from '@/lib/cn';

const props = defineProps<{
  open: boolean;
  title?: string;
  description?: string;
  class?: string;
  hideClose?: boolean;
}>();

const emit = defineEmits<{ 'update:open': [value: boolean] }>();
</script>

<template>
  <DialogRoot :open="props.open" @update:open="(v: boolean) => emit('update:open', v)">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm data-[state=open]:animate-fade-in" />
      <DialogContent
        :class="cn(
          'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
          'rounded-2xl bg-surface shadow-pop outline-none border border-border',
          'data-[state=open]:animate-slide-in-up',
          props.class,
        )"
      >
        <header v-if="title || $slots.header" class="flex items-start justify-between gap-4 px-6 pt-6 pb-3">
          <div class="flex flex-col gap-1">
            <DialogTitle v-if="title" class="text-title text-text">{{ title }}</DialogTitle>
            <DialogDescription v-if="description" class="text-caption text-text-muted">
              {{ description }}
            </DialogDescription>
            <slot name="header" />
          </div>
          <DialogClose
            v-if="!hideClose"
            class="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-bg hover:text-text transition-colors"
            aria-label="Close"
          >
            <X class="h-4 w-4" />
          </DialogClose>
        </header>
        <div class="px-6 pb-6">
          <slot />
        </div>
        <footer v-if="$slots.footer" class="border-t border-border px-6 py-4 flex items-center justify-end gap-2">
          <slot name="footer" />
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
